/** Collision-safe enough for a local, single-browser app. */
export function uid(prefix = ''): string {
  return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

/**
 * The letter group a client name belongs to, for the A-Z style filter.
 *
 * Not simply the first character. Account codes run letters-then-digits --
 * C100, T127, UC45 -- and there the whole letter run is the group, so UC45
 * files under UC rather than being mixed in with U. A name that is not a code
 * keeps its first letter, so "Acme Ltd" files under A.
 *
 * The digit is what distinguishes the two: without it, "Providentia Wealth"
 * would file under PROVIDENTIA and every such client would get a group of its
 * own.
 *
 * Anything not starting with a letter files under # -- a real bucket rather
 * than a crash or an invisible row.
 *
 * Character classes are written out rather than using an escape: this file has
 * been edited by script, and a lost backslash in a regex has silently changed
 * behaviour here before.
 */
/**
 * A list of ids remembered on this device, for a filter that should survive
 * leaving the page.
 *
 * Wrapped in try/catch at both ends: localStorage throws outright in a private
 * window and in some embedded browsers, and a filter failing to be remembered
 * must never take the screen down with it.
 *
 * Reads are PRUNED against what currently exists. A stored id whose client has
 * since been deleted would otherwise filter the list down to nothing, with the
 * thing causing it no longer on screen to un-tick.
 */
export function readStoredIds(key: string, exists: (id: string) => boolean): string[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string' && exists(v))
  } catch {
    return []
  }
}

export function writeStoredIds(key: string, ids: readonly string[]): void {
  try {
    if (ids.length === 0) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Not being remembered is a smaller problem than not working.
  }
}

export function clientPrefix(name: string): string {
  const t = String(name).trim().toUpperCase()
  if (!t) return '#'

  const coded = t.match(/^([A-Z]+)[0-9]/)
  if (coded) return coded[1]

  const first = t.match(/^[A-Z]/)
  return first ? first[0] : '#'
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/** Turn `jane.doe@x.com` into `Jane Doe`. */
export function nameFromEmail(email: string): string {
  return email
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function isEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}

/** Blend two hex colours. `amt` 0 = all `hex`, 1 = all `to`. */
export function mix(hex: string, to: string, amt: number): string {
  const parse = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
  const a = parse(hex)
  const b = parse(to)
  return (
    '#' +
    a
      .map((v, i) => Math.round(v + (b[i] - v) * amt).toString(16).padStart(2, '0'))
      .join('')
  )
}

/**
 * Push the accent colour and its derived tints onto the document.
 *
 * Only the workspace accent. The prospect screens take a fixed palette set
 * in styles.css rather than one derived from this: a complement computed off
 * whatever accent was chosen kept landing on muddy colours, and a palette
 * somebody picked by eye beats one a formula argued for.
 */
export function applyAccent(accent: string): void {
  const r = document.documentElement.style
  r.setProperty('--ac', accent)
  r.setProperty('--ac-ink', mix(accent, '#000000', 0.12))
  r.setProperty('--ac-hover', mix(accent, '#000000', 0.12))
  r.setProperty('--ac-soft', mix(accent, '#FFFFFF', 0.88))
  r.setProperty('--ac-line', mix(accent, '#FFFFFF', 0.62))
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n))
}

export function pluralise(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`
}

/* ---------- password hashing ----------
   This app has no server, so there is nobody to authenticate against. The hash
   only stops a password sitting in localStorage as plain text; it is not a
   security boundary. Anyone with access to the browser profile can read or
   replace the stored data outright. See README.md.
------------------------------------------ */

export function randomSalt(): string {
  const a = new Uint8Array(16)
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(a)
  else for (let i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256)
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function hashPassword(password: string, salt: string): Promise<string> {
  const input = salt + ':' + password
  // crypto.subtle needs a secure context — absent when the built app is opened
  // straight off the filesystem, so fall back rather than break sign-in.
  if (globalThis.crypto?.subtle) {
    const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
    return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
  }
  let h1 = 0x811c9dc5
  let h2 = 0x01000193
  for (let i = 0; i < input.length; i++) {
    h1 = Math.imul(h1 ^ input.charCodeAt(i), 0x01000193) >>> 0
    h2 = Math.imul(h2 + input.charCodeAt(i) * (i + 1), 0x85ebca6b) >>> 0
  }
  return 'fallback:' + h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')
}

/** CSV cell escaping: quote anything containing a comma, quote or newline. */
export function csvCell(v: string | number): string {
  const s = String(v)
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

/**
 * U+FEFF. Excel reads a .csv as the system codepage unless the file opens
 * with this, and then shows every non-ASCII character as mojibake.
 */
const BOM = '\uFEFF'

/**
 * The CSV text as it must be written to disk.
 *
 * Separate from downloadFile only so it can be tested: downloadFile needs a
 * DOM, this is the part that decides the bytes.
 */
export function withCsvBom(content: string, type: string): string {
  if (!type.startsWith('text/csv') || content.startsWith(BOM)) return content
  return BOM + content
}

export function downloadFile(filename: string, content: string, type: string): void {
  // The server already puts a BOM on its CSVs -- and it never survives the
  // trip. The body arrives through fetch, and Response.text() decodes with
  // the Encoding Standard's "UTF-8 decode", which STRIPS a leading BOM by
  // definition. By the time the text reaches here the marker is gone, so the
  // Blob is written without one and Excel falls back to Windows-1252: a
  // narrow no-break space (U+202F, which Intl puts before AM/PM) came out as
  // "10:00:00â€¯AM". Putting it back is the only place that
  // can, since this is where the bytes are finally decided.
  const blob = new Blob([withCsvBom(content, type)], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
